/**
 * Broker-logo helpers shared by every account tile (mobile Settings card, Select Account list, desktop
 * header / accounts sheet, edit-account).
 *
 * Why this exists: the Vybe gateway advertises its brand mark as a REMOTE url (`/brand/vybe-trader.svg`
 * on the gateway host) whenever a public base url is configured, and the CRM's synced-account listing
 * carries no logo at all unless `Crm:Connect:LogoUrl` is set. Both reached the terminal as something
 * `BrokerLogo` had to fetch — and on an iPhone PWA that fetch fails after a suspend or a cold refresh,
 * so the tile fell back to a grey "VY" initials tile (SR Global, 2026-09-08). Our own mark is bundled
 * here as a data-URI so painting it never depends on the network, and every account record that ever
 * stored the remote form is normalised to it.
 *
 * Geometry mirrors `VybeLogo.tsx` and the gateway's `VybeBrandMark.cs` (six rects on a 250×250 viewBox).
 * The fill is a literal, not `currentColor`: this is consumed as an `<img src>` where nothing inherits.
 */

/** Route the Vybe gateway serves its mark from — used only to RECOGNISE the remote form. */
export const VYBE_BRAND_LOGO_PATH = "/brand/vybe-trader.svg";

const VYBE_BRAND_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 250 250" width="250" height="250" role="img" aria-label="Vybe Trader">' +
  "<title>Vybe Trader</title>" +
  '<g fill="#fafafa">' +
  '<rect x="15" y="67" width="40" height="30"/>' +
  '<rect x="35" y="110" width="80" height="30"/>' +
  '<rect x="95" y="67" width="140" height="30"/>' +
  '<rect x="55" y="153" width="40" height="30"/>' +
  '<rect x="145" y="110" width="40" height="30"/>' +
  '<rect x="145" y="153" width="40" height="30"/>' +
  "</g></svg>";

function toBase64(ascii: string): string {
  if (typeof btoa === "function") return btoa(ascii);
  // Node (vitest) fallback — the SVG is pure ASCII so Buffer's default encoding is exact.
  return (globalThis as unknown as { Buffer: { from(s: string): { toString(enc: string): string } } }).Buffer.from(ascii).toString("base64");
}

/** The bundled, offline-safe form of the Vybe Trader mark. */
export const VYBE_BRAND_LOGO_DATA_URI = "data:image/svg+xml;base64," + toBase64(VYBE_BRAND_SVG);

/** True for any form of OUR mark: the gateway's hosted route (any host) or the bundled data-URI. */
export function isVybeBrandLogoUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  if (url === VYBE_BRAND_LOGO_DATA_URI) return true;
  return url.split("?")[0].endsWith(VYBE_BRAND_LOGO_PATH);
}

/** The slug the CRM/gateway use for the Vybe-engine gateway (`sig-vybe`); custom gateways never match. */
export function isVybeGatewaySlug(slug: string | null | undefined): boolean {
  if (!slug || slug.startsWith("custom:")) return false;
  return /vybe/i.test(slug);
}

/** "Vybe Trader" / "Vybe Trader Live" — the company the CRM stamps onto its own accounts. */
export function isVybeCompanyName(name: string | null | undefined): boolean {
  return /^vybe\s*trader\b/i.test((name ?? "").trim());
}

/**
 * The value to PERSIST on an account record: the remote form of our own mark becomes the bundled
 * data-URI (nothing to fetch, nothing to fail); every other logo is stored as given.
 */
export function normalizeBrokerLogoUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  return isVybeBrandLogoUrl(url) ? VYBE_BRAND_LOGO_DATA_URI : url;
}

/**
 * Logo for a gateway descriptor (the CRM's synced-account `gateway` block, a connect-token payload):
 * the advertised logo, normalised — or our own mark when the descriptor is the Vybe gateway and the
 * CRM sent none (`Crm:Connect:LogoUrl` unset).
 */
export function gatewayLogoUrl(gateway: {
  logoUrl?: string | null;
  gatewaySlug?: string | null;
  companyName?: string | null;
}): string | undefined {
  return (
    normalizeBrokerLogoUrl(gateway.logoUrl) ??
    (isVybeGatewaySlug(gateway.gatewaySlug) || isVybeCompanyName(gateway.companyName)
      ? VYBE_BRAND_LOGO_DATA_URI
      : undefined)
  );
}
